/* eslint-disable @typescript-eslint/no-this-alias */
/* eslint-disable no-console */
import SPLM from '@atlas/api-javascript-sdk/no/splm';
import '@atlas/api-javascript-sdk/no/Object';

import {
    isSdkObject,
    isSdkPropertyName,
    isSdkFunctionName,
    jsonToSdk,
    mergeObjects,
    getSdkObjectProperty,
    isEmptyObject,
    withApiQueueEnabled
} from '@atlas/api-utils';

/**
 *
 * @param response
 * @param methodName
 */
function unwrapResponseObjectForCall(response, methodName) {
    // Unpick the wrapping around the data returned by the function call.
    if (response instanceof Object && methodName in response) {
        response = response[methodName];
    } else if (response instanceof Object && response.skipped) {
        return response;
    } else {
        // We also get here if a call would normally produce an error, but this was converted
        // to a warning. As the call generates an error, there is no response for the server to
        // return to us, but as the error has become a warning, we get a response which contains
        // the warning instead. If this has happened, use the message for the API warning as the
        // text for the error that we throw.
        const message = response.API_Warning_Message
            ? response.API_Warning_Message + ' (' + response.API_Warning + ')'
            : 'Missing response from server - expecting reply for ' + methodName;
        const err = new Error(message);
        // TODO: Include original request???
        err.api_result = response;
        throw err;
    }

    return response;
}

/**
 *
 * @param response
 * @param objectHandler
 */
function wrapReturnValue(response, objectHandler) {
    // The Atlas server will return empty objects to indicate
    // no response of any kind.
    if (isEmptyObject(response)) {
        response = undefined;
    }

    if (response instanceof Object) {
        // Convert the returned JSON data into a new SPLM object of the same class.
        response = jsonToSdk(response);

        // ... and then apply the Proxy to that SDK object
        response = new Proxy(response, objectHandler);
    }

    return response;
}

/**
 *
 * @param result
 * @param promise
 */
function assignExtraDataToReturnValue(result, promise) {
    if (result !== undefined && promise !== undefined) {
        result.canChain = true;
        result.isResolved = false;
        result.resultReadyPromise = promise
            .then((val) => {
                result.isResolved = true;
            })
            .catch((err) => {
                result.isResolved = true;
            });
    }
}

/**
 *
 * @param localVariables
 * @param propName
 */
function getVariableForPropName(localVariables, propName) {
    for (const localVariableDescriptor of localVariables) {
        if (localVariableDescriptor[2] === propName) {
            return localVariableDescriptor[0];
        }
    }
}
/**
 *
 * @param localVariables
 * @param varName
 */
function getPropNameForVariable(localVariables, varName) {
    for (const localVariableDescriptor of localVariables) {
        if (localVariableDescriptor[0] === varName) {
            return localVariableDescriptor[2];
        }
    }
}
/**
 *
 * @param request
 * @param methodName
 */
function getLocalVariablesForMethod(request, methodName) {
    // The Atlas SDK used to add a property to the request called
    // 'LocalVariable'. This was an array that associated the name
    // of a local variable that would be assigned to each value
    // returned from a function call. The new approach appears to
    // document this by populating the request object with an object
    // named after the called method and with entries for each return
    // parameters of that function (and prefixed with '%').
    // We transform this data back into the format that this code
    // currently expects.

    const oldStyleVariableTable = request.LocalVariable;

    const newStyleVariableTable = Object.entries(request[methodName])
        .filter(([propName, _varName]) => propName[0] === '%')
        .map(([propName, varName]) => [varName, methodName, propName.substr(1)]);

    return oldStyleVariableTable || newStyleVariableTable;
}

/**
 *
 * @param err
 */
function defaultRejectedPromiseHandler(err) {
    console.error('ProxyHandler promise error: ', err);
}

/**
 * @description
 * A class to use as a handler with the Javascript Proxy mechanism. When an
 * instance of this class is used to proxy one of the Atlas SDK objects, it adds
 * extra methods to the object to facilitate getting and setting property values
 * on that object, as well as directly calling the methods defined for that object.
 *
 * Any properties read from the proxied object, or any values returned by methods on
 * that object, will have been converted from the raw JSON data returned by the Atlas
 * server into the corresponding SDK objects (assuming that the required SDK classes
 * have been imported into the client session) and then wrapped in a proxy layer.
 *
 * The handler object itself is constructed from a function which can send an API request
 * object to the Atlas server and return a Promise object that resolves to the
 * response for that request.
 * @example
 * const handler = new SdkObjectProxyHandler(sendApiPromise);
 * @description
 * Once the handler object is created, it can be used for creating a
 * proxy for any object created from the SPLM classes, or the SPLM classes themselves
 * (in which case any object instantated from the proxied class will be automatically
 * proxied), or any other Javascript object (e.g. an array of SPLM objects).
 * @example
 * const splmFaceProxy = new Proxy(new SPLM.Face(1234), handler);
 * @description
 * Each property of the underlying SDK object results in a pair of
 * get<prop> and set<prop> methods appearing on the proxy for that object. When
 * called these issue a request to the Atlas server to retrieve (or set) the property,
 * and they return a promise for the result of this operation.
 * @example
 * // Issue request to the Atlas server to retrieve the
 * // Layer property for the SPLM.Face object
 * const layerPromise = splmFaceProxy.getLayer();
 * @description
 * In a similar fashion, the methods defined for the SDK object result in an
 * additonal method named call<method> on the proxy object. This takes the same
 * parameters as the raw method on the target object, but instead of returning a
 * request object, it returns a promise that is resolved to the result of issuing
 * that request.
 * @example
 * // Set a new Undo markpoint.
 * const sessionProxy = new Proxy(new SPLM.Session(), handler);
 * const undoMarkPromise = sessionProxy.callSetUndoMark('Visible', 'My Markpoint Name');
 * @description
 * The simplest way to use this proxying mechanism is to
 * wrap the SPLM object, the root of the SDK class hierarchy, in a Proxy.
 * From this starting point, any object that is created from any of the
 * classes referenced from here will be automatically wrapped.
 * @example
 * // Wrap the entire SPLM hierarchy
 * const splm = new Proxy(SPLM, handler);
 * // Create a new session object. Use the proxied "splm" hierarchy
 * // so that the created object is automatically proxied.
 * const session = new splm.Session();
 * // Set a new Undo markpoint.
 * const undoMarkPromise = session.callSetUndoMark('Visible', 'My Markpoint Name');
 * // List all features in the work part
 * const workPart = session.Parts.Work;
 * const featuresPromise = workPart.Features.callGetFeatures();
 */
export class SdkObjectProxyHandler {
    /**
     * @param {Function} sendApiPromise - a function to send an API request object to the Atlas server, and returning a Promise for the result from the server.
     * @param rejectedPromiseHandler
     */
    constructor(sendApiPromise, rejectedPromiseHandler = defaultRejectedPromiseHandler) {
        this.sendApiPromise = sendApiPromise;
        this.rejectedPromiseHandler = rejectedPromiseHandler;
    }

    /**
     * @description Trap for accessing properties from the proxy object.
     * @param {object} target - the proxied object
     * @param {string} prop - the name of the property being accessed (NOTE: not **always** a string)
     * @returns {any} For basic types the raw value is returned. When the property is an object, a new Proxy is
     * generated for that value.
     */
    // eslint-disable-next-line complexity
    get(target, prop) {
        const value = target[prop];

        if (typeof prop !== 'string') {
            // No nothing for these special properties
        } else if (prop === 'prototype') {
            // No nothing for these special properties
        } else if (prop === '_target') {
            // If we want the raw (unproxied) pointer, return that
            //console.debug('get-target-object: returning raw object', target);
            return target;
        } else if (prop[0] === '_') {
            // Do nothing to properties starting with "_" (e.g. "_data" or "_d").
            // This is the SDK object trying to access its own internal structure
            // so leave it alone.

            // Change of plan. The "get" properties on the SDK objects are so hideous
            // right now that we have to delve into the "_d" bucket to avoid going
            // through them. If we don't apply a proxy layer to this object, this will
            // then strip-off the proxy layer
            if (value instanceof Object) {
                return new Proxy(value, this);
            }

            return value;
        } else if (value === undefined && prop.startsWith('call') && isSdkObject(target) && isSdkFunctionName(target, prop.substring(4))) {
            // Q. How would we allow the caller to assign (local or global) variables to the return values of this call?
            //    When the returned function is called, do we create a Promise object to return, and then add a "setVariable"
            //    method to that object and which actually *modifies* in place the request object?

            if (!this.canIssueRequest(target)) {
                throw new Error('Cannot "' + prop + '()" for SDK object with no ObjectId');
            }

            const methodName = prop.substring(4);
            return this.handleCallMethod(target, methodName, false);
        } else if (value === undefined && prop.startsWith('delayCall') && isSdkObject(target) && isSdkFunctionName(target, prop.substring(9))) {
            // Q. How would we allow the caller to assign (local or global) variables to the return values of this call?
            //    When the returned function is called, do we create a Promise object to return, and then add a "setVariable"
            //    method to that object and which actually *modifies* in place the request object?

            if (!this.canIssueRequest(target)) {
                throw new Error('Cannot "' + prop + '()" for SDK object with no ObjectId');
            }

            const methodName = prop.substring(9);
            return this.handleCallMethod(target, methodName, true);
        } else if (value === undefined && prop.startsWith('set') && isSdkObject(target) && isSdkPropertyName(target, prop.substring(3))) {
            if (!this.canIssueRequest(target)) {
                //restoreApiQueue(oldApiQueueState);
                throw new Error('Cannot "' + prop + '()" for SDK object with no ObjectId');
            }

            const propName = prop.substring(3);
            return this.handleSetProperty(target, propName);
        } else if (value === undefined && prop.startsWith('delaySet') && isSdkObject(target) && isSdkPropertyName(target, prop.substring(8))) {
            if (!this.canIssueRequest(target)) {
                //restoreApiQueue(oldApiQueueState);
                throw new Error('Cannot "' + prop + '()" for SDK object with no ObjectId');
            }

            const propName = prop.substring(8);
            return this.handleSetProperty(target, propName, true);
        } else if (
            value === undefined &&
            prop.startsWith('get') &&
            isSdkObject(target) &&
            (isSdkPropertyName(target, prop.substring(3)) || isSdkFunctionName(target, 'Get' + prop.substring(3)))
        ) {
            if (!this.canIssueRequest(target)) {
                throw new Error('Cannot "' + prop + '()" for SDK object with no ObjectId');
            }

            const propName = prop.substring(3);
            return this.handleGetProperty(target, propName, false);
        } else if (
            value === undefined &&
            prop.startsWith('delayGet') &&
            isSdkObject(target) &&
            (isSdkPropertyName(target, prop.substring(8)) || isSdkFunctionName(target, 'Get' + prop.substring(8)))
        ) {
            if (!this.canIssueRequest(target)) {
                throw new Error('Cannot "' + prop + '()" for SDK object with no ObjectId');
            }

            const propName = prop.substring(8);
            return this.handleGetProperty(target, propName, true);
        } else if (target instanceof Promise) {
            // prop => "then" or "catch"
            if (typeof value === 'function') {
                // Do we need to wrap promises in a Proxy layer? Isn't the result
                // going to be already wrapped?
                return new Proxy(value.bind(target), this);
            }
        } else if (value instanceof Object) {
            // // If this object is a SPLM thing, and the value being returned is an
            // // SPLM thing, and the value doesn't know what it's ObjectId is but
            // // the target one does, then provide the value object with a temporary id
            // // which identifies it as a sub-object of this one.
            // if (isSdkObject(target) && isSdkObject(value) && (value.ObjectId === undefined && value._objectId === undefined)) {
            //     // NOTE: We don't want to play this trick if the target's actual ObjectId is something other
            //     // than a simple number. If it is the result of a function call (which might create objects)
            //     // then we shouldn't use it. Of course this gets trickier if the target's ObjectId is a
            //     // reference to a local/global variable...
            //     const thisObjectId = (typeof target.ObjectId === "number") ? target.ObjectId : target._objectId;
            //     if (thisObjectId !== undefined) {
            //         const valueObjectId = new target.constructor( thisObjectId );
            //         valueObjectId[prop]; // Reading the property instantiates it in this object
            //         value._objectId = valueObjectId;
            //     }
            // }

            return new Proxy(value, this);
        }

        return value;
    }

    /**
     * @description Trap for setting properties on the proxied object. Verifies that the new value is
     * not a Promise (we cannot send Promises to the Atlas server, so they cannot ever be good data in
     * an SDK object). If the new value is a proxy of another SDK object, the proxy layer is removed
     * (currently by cloning the value)
     * @param {object} target - the proxied object
     * @param {string} prop - the name of the property being accessed
     * @param {object} value - the new value for the property
     * @returns {boolean} true if the assignment succeeded
     */
    set(target, prop, value) {
        value = this.unproxyValue(value);
        target[prop] = value;
        return true;
    }

    /**
     * @param target
     * @param thisArg
     * @param argumentsList
     * @returns {any}
     * @description Trap for using a proxied object as a function. We generate proxies for
     * methods of Atlas API classes and this trap ensures that the values returned by these
     * function calls are themselves wrapped in a proxy.
     */
    apply(target, thisArg, argumentsList) {
        const realThisArg = this.unproxyValue(thisArg);
        const result = target.bind(realThisArg)(...argumentsList);
        if (result instanceof Object) {
            return new Proxy(result, this);
        }
        return result;
    }

    /**
     * @description Trap for using a proxied object as a constructor. We generate proxies for
     * all of the Atlas API classes and this trap ensures that the object created from those
     * classes is itself wrapped in a proxy.
     * @param {ObjectConstructor} Target - the constructor function
     * @param {Array} args - the arguments passed to the constructor.
     * @returns {object} the proxied instance of the constructor
     */
    construct(Target, args) {
        const object = new Target(...args);
        return new Proxy(object, this);
    }

    //////////////////////////////////////////////////////////////////////////////////////////////////

    /**
     *
     * @param {boolean} [delayRequest]
     * @param {Function} [requestSentCb] - This callback is not supported in the current delegator implementation, should be removed in the future.
     * @returns {Promise} a promise that resolves to the result of the API request
     */
    sendApi(delayRequest = false, requestSentCb = undefined) {
        // Empty the SPLM.api array so that these
        // requests do not get issued a second time.
        const api = SPLM.api.splice(0);
        const options = {
            autoFlushRequest: !delayRequest,
            requestSentCb: requestSentCb
        };
        return this.sendApiPromise(api, options);
    }

    getTargetObjectId(target) {
        return target._d.ObjectId !== undefined ? target._d.ObjectId : target._objectId;
    }

    /**
     * @summary Simplifies the ObjectId property for Atlas requests.
     * @description
     * If a property is requested from a subobject of an Atlas object and if the
     * ObjectId value for the subobject isn't already known, the SDK will automatically
     * generate an ObjectId value for the subobject. This ObjectId is defined in terms of the
     * property of the starting object used to access the subobject. For example,
     * given an Extrude builder object "b" (where all that is known about this object
     * is the builder's ObjectId), one can access a subproperty like this
     *
     *     builder.Limits.EndExtend.Value
     *
     * and the SDK may generate an ObjectId for the request like this
     *
     *     { ObjectId: {
     *         Value: { '%value': '$123' },
     *         ObjectId: {
     *            EndExtend: { '%end_extend': '$122' },
     *            ObjectId: {
     *                Limits: {
     *                   ....
     *
     * and so on until it reaches an object with a known ID. This approach is
     * used when the SPLM.api queue isn't being used. This syntax used to
     * be supported and would identify the SPLM.Expression object. However this
     * syntax is no-longer supported (it generates warnings about nested objects)
     * and if we send a request object using this as its ObjectId, the returned
     * object is messed up and contains all of the properties used to identify the
     * subobject.
     *
     * When using the SPLM.api mechanism, which we do when issuing requests to the
     * Atlas server, accessing this subproperty instead pushes a number of requests
     * to the queue and returns a simpler ID string just consisting of a reference
     * to the variable created by the final property request. In this case above we
     * get these requests pushed
     *
     *   [
     *     { ObjectId: "builder's id", Limits: { '%limits', '$121' }, ... }
     *     { ObjectId: { '$var': '$121' }, EndExtend: { '%end_extend', '$122' }, ...}
     *     { ObjectId: { '$var': '$122' }, Value: { '%value', '$123' }, ...}
     *   ]
     *
     * and the request object then has an ObjectId which is simply this
     *
     *     { '$var': '$123' }
     *
     * This function converts the ObjectId specification of the first form into
     * the second form by pushing the requests which define the variables into the
     * SPLM.api queue, and then constructing the simplified variable reference
     * from the name of the variable that was defined by each request.
     * @param {object} id - the ObjectId value to be requested
     * @returns {object} a simplified ObjectId
     */
    unwindTargetObjectId(id) {
        if (id instanceof Object && id.Class !== undefined) {
            if (id.ObjectId) {
                id.ObjectId = this.unwindTargetObjectId(id.ObjectId);
            }
            // Identify the property which names the variable generated by the ID request.
            // This will be the one that isn't called 'Class' or 'ObjectId'. This will
            // be something like { ..., Value: { '%value': '$1234' }, .... }. This
            // object will have a sub-property with a key name starting with '%' which
            // corresponds to a value string starting with '$'
            const propName = Object.keys(id)
                .filter((key) => key !== 'Class' && key !== 'ObjectId')
                .pop();
            if (propName) {
                // Find the property which names the variable
                const varPropName = Object.keys(id[propName])
                    .filter((key) => key[0] === '%')
                    .pop();
                if (varPropName) {
                    SPLM.api.push(id);
                    id = { $var: id[propName][varPropName] };
                }
            }
        }
        return id;
    }

    createRequestObject(target) {
        const request = new target.constructor();
        const targetObjectId = this.getTargetObjectId(target);
        if (targetObjectId !== undefined) {
            if (targetObjectId instanceof Object && 'Class' in targetObjectId) {
                //console.warn( 'Creating Atlas request for object with ObjectId =', targetObjectId );
                request._d.ObjectId = this.unwindTargetObjectId(targetObjectId);
            } else {
                request._d.ObjectId = targetObjectId;
            }
        }
        return request;
    }

    canIssueRequest(target) {
        // Q. What about UserObjectId?
        // Q. What about ObjectId values which are themselves objects?
        const targetObjectId = this.getTargetObjectId(target);
        return targetObjectId !== undefined || !(target.constructor instanceof SPLM.Object);
    }

    unproxyValue(value) {
        // Ensure that "value" is not a proxy object.
        if (value instanceof Object) {
            // We endow all proxied objects with a "_target" property that returns
            // the original object.
            value = value._target || value;
        }
        return value;
    }

    handleGetProperty(target, propName, delayRequest) {
        const self = this;

        return function (ignoreCache = false) {
            // If this object already knows this value, return that value
            // (properly wrapped) unless we were told to ignore it.
            // Don't return subobjects this way unless they know their
            // ObjectId value. Always fetch subobjects properly so that
            // we can know their correct [non-nested] ObjectId value.
            if (!ignoreCache) {
                const propValue = getSdkObjectProperty(target, propName);
                if (propValue !== undefined && (!isSdkObject(propValue) || propValue.ObjectId instanceof Array)) {
                    const resultValue = wrapReturnValue(propValue, self);
                    const promise = Promise.resolve(resultValue);
                    promise.result = () => resultValue;
                    return promise;
                }
            }

            return withApiQueueEnabled(() => {
                // Asking the target object for the property should populate the
                // SPLM.api array with a new request. The object that it returns
                // to us is different ... we're supposed to use that if we need
                // to pass this result to a new function call.
                let request = undefined;

                const pristineTargetObject = self.createRequestObject(target);

                /////////////////////////////////////////////////////////////////////////////
                // HACK: The SDK is somewhat messed up. It thinks that a number of properties
                // are actually functions starting with "Get...", but the Atlas server rejects
                // this. For now try overriding the SDK behavior...
                if (propName in pristineTargetObject) {
                    // Request the property properly using the SDK object
                    request = pristineTargetObject[propName];
                } else if ('Get' + propName in pristineTargetObject) {
                    // Bypass the SDK object's getter and request the property using the
                    // name that we expect it to have.
                    request = {};
                    request.Class = pristineTargetObject.Class;
                    request.ObjectId = pristineTargetObject.ObjectId;
                    request[propName] = {};
                }
                // END HACK:
                /////////////////////////////////////////////////////////////////////////////

                // The result of the property look-up may be either another SDK object
                // (for sub-properties) or it may be an object which represents the
                // resultant value. For this latter case there is a "result" property
                // which is a function, so if this isn't present then the value that
                // was returned can be used as the return value directly.
                if (request instanceof Object && typeof request.result !== 'function') {
                    const resultValue = wrapReturnValue(request, self);
                    const promise = Promise.resolve(resultValue);
                    assignExtraDataToReturnValue(resultValue, promise);
                    promise.isResolved = true;
                    promise.result = () => resultValue;
                    return promise;
                }

                let returnResult = undefined;
                let requestSentCb = () => {
                    if (returnResult !== undefined && returnResult.canChain !== undefined) {
                        returnResult.canChain = false;
                    }
                };

                const promise = self.sendApi(delayRequest, requestSentCb).then((responses) => {
                    // Take the result object that was returned in response to the last request
                    // object to be the response for this method.
                    let response = responses.pop();

                    // Update the target object with the returned value(s). Do this before we wrap the
                    // response in a proxy.
                    // NOTE: we also do this merging *before* we unwrap the returned value. This is
                    // because the raw response from the server matches the structure of the SDK object
                    // in that it has Class, ObjectId and <prop> values defined on it at this point
                    // (which can be merged into "target"). If we unwrap the response so we get down
                    // to the bare value, we would then need to re-wrap it with the name of the property
                    // on target that needed to be updated.
                    mergeObjects(target, response);

                    // The Atlas server wraps the return value for the call inside another object. Unpick
                    // this to retrieve the data we want to return.
                    response = unwrapResponseObjectForCall(response, propName);

                    // Convert the response back into SDK objects (if we can) and add a Proxy to them
                    response = wrapReturnValue(response, self);

                    return response;
                });

                /**
                 * @returns {object} an object which represents the value that will be returned for this property. This "result"
                 * object can be passed as input to another function call to tell the Atlas server to chain together the
                 * output of one call and to feed that into a second call.
                 * NOTE: This relies on the LocalVariable mechanism in the Atlas server, and these values are only valid
                 * for the duration of the processing of one single request message. Any additional requests that are to
                 * be made using this result object must be sent to the server as part of the same API request array.
                 */
                returnResult = wrapReturnValue(request.result(), self);
                promise.result = () => returnResult;

                assignExtraDataToReturnValue(returnResult, promise);
                promise.catch(self.rejectedPromiseHandler);

                return promise;
            });
        };
    }

    handleSetProperty(target, propName, delayRequest) {
        const self = this;

        return function (value) {
            return withApiQueueEnabled(() => {
                // Throw an exception if we are passed Promise objects as the new value for this property.
                // We can't send a Promise to the Atlas server (because we don't know what it resolves to)
                // and we can't wait for the promise to be resolved (because this would mean that this
                // call function isn't queued up at the correct time and will be resolved out-of-sequence).
                if (value instanceof Promise) {
                    throw new Error('Cannot set properties of SDK objects to promise objects');
                }

                // Ensure that "value" is not a proxy object.
                value = self.unproxyValue(value);

                // Create a new request object so that the only data we send to the Atlas
                // server is just the one property that we are changing.
                // Actually, that isn't quite true. We send two pieces of data - the property
                // being set *and* the object id of the target. This may also end up changing
                // the target object (for instance if the ObjectId is a recipie to create an
                // object in the Atlas session)
                const pristineTargetObject = self.createRequestObject(target);

                // Perform the value assignment
                pristineTargetObject[propName] = value;

                // Generate a second request to get the value back from the server. This allows
                // the server to adjust the given value if it wants to and ensures that
                // the client object contains the updated value.
                const unusedRequestObject = pristineTargetObject[propName];

                // Also update the original target object with the new value. We know
                // that this is a writable property because the above assignment on the
                // pristineTargetObject didn't throw any error. By updating the original
                // target now this means (1) any requests to retrieve the value will get
                // this new value immediately [whether or not the cache is bypassed in the
                // the get<prop> request], and (2) when we later on come to merge in the
                // result returned from the server, this won't have any issues if the new
                // value is an Atlas object which has a different ObjectId to the old value.
                target._d[propName] = value;

                const promise = self.sendApi(delayRequest).then((responses) => {
                    // Take the last response object that was returned to be the
                    // result for the setRequest call.
                    let response = responses.pop();

                    // Update the target object with the returned value(s). Do this before we wrap the
                    // response in a proxy.

                    mergeObjects(target, response);

                    // The Atlas server wraps the return value for the call inside another object. Unpick
                    // this to retrieve the value that was returned for this property.
                    response = unwrapResponseObjectForCall(response, propName);

                    // Convert the response back into SDK objects (if we can) and add a Proxy to them
                    response = wrapReturnValue(response, self);

                    return response;
                });

                promise.catch(self.rejectedPromiseHandler);

                return promise;
            });
        };
    }

    handleCallMethod(target, methodName, delayRequest) {
        // Q. How would we allow the caller to get hold of the local variables assigned to the return values of this call?

        const self = this;

        return function (...argumentsList) {
            return withApiQueueEnabled(() => {
                // Throw an exception if we are passed Promise objects as parameters to this call. We
                // can't send a Promise to the Atlas server (because we don't know what it resolves to)
                // and we can't wait for the promise to be resolved (because this would mean that this
                // call function isn't queued up at the correct time and will be resolved out-of-sequence).
                if (argumentsList.find((x) => x instanceof Promise) !== undefined) {
                    throw new Error('Cannot send a promise to the Atlas server');
                }

                // Call the method on a new copy of the target object. This avoids polluting the source object (target)
                // with the data to trigger the same call again if that object is ever sent directly to the Atlas server.
                const pristineTargetObject = self.createRequestObject(target);
                const funcToCall = target[methodName].bind(pristineTargetObject);

                // Call the bound method with the arguments passed to this function. This returns
                // an object that acts as a stand-in for the result of the API request that has not
                // yet been sent to the server.
                const request = funcToCall(...argumentsList);

                // This is the request object that is to be sent to the Atlas server. It contains
                // the specifications for the local variables that are assigned to the output
                // parameters of the function call.
                const apiRequest = SPLM.api[SPLM.api.length - 1];

                const localVariables = getLocalVariablesForMethod(apiRequest, methodName);

                let returnResult = undefined;
                let requestSentCb = () => {
                    if (returnResult !== undefined && returnResult.canChain !== undefined) {
                        returnResult.canChain = false;
                    }
                };

                const promise = self.sendApi(delayRequest, requestSentCb).then((responses) => {
                    // Take the last responses object that was returned to be the
                    // response for this call<method> call.
                    let response = responses.pop();

                    // The Atlas server wraps the return value for the call (the output arguments
                    // from the function) inside another object which associates it back to the
                    // original request. Atlas can process a single request object containing multiple
                    // method calls, so it needs to identify which method call the returned values
                    // correspond to. Unpick this to retrieve the actual value from this one
                    // method call.
                    response = unwrapResponseObjectForCall(response, methodName);

                    // Convert the response back into SDK objects (if we can) and add a Proxy to them
                    const proxiedResponse = wrapReturnValue(response, self);

                    // Add a ".result()" function to the response object which identifies the
                    // returned property corresponding to the API_RESULT output argument.
                    // We have to do this after wrapping the return value because that involves
                    // a clone of the result object (when creating the SDK objects from the
                    // plain JSON one) and this won't copy any assigned functions.
                    if (proxiedResponse instanceof Object && request instanceof Object && typeof request.result === 'function') {
                        proxiedResponse.result = function () {
                            const apiResultDescriptor = request.result();
                            const apiResultVariable = apiResultDescriptor.$var || apiResultDescriptor.ObjectId.$var;
                            const apiResultPropName = getPropNameForVariable(localVariables, apiResultVariable);
                            // NOTE: When the result function is called on the returned proxy object,
                            //       the handler will automatically wrap the return value in a new
                            //       proxy. There is no need to do this for the return value here.
                            const result = response[apiResultPropName];
                            // However we *do* still need to run the json-to-sdk conversion if we want the
                            // returned object to have the expected Atlas SDK methods appropriate to its class.
                            return result instanceof Object ? jsonToSdk(result) : result;
                        };
                    }

                    return proxiedResponse;
                });

                /**
                 * @returns {object} an object which represents the API_RESULT value of this function call. This "result"
                 * object can be passed as input to another function call to tell the Atlas server to chain together the
                 * output of one call and to feed that into a second call.
                 * NOTE: This relies on the LocalVariable mechanism in the Atlas server, and these values are only valid
                 * for the duration of the processing of one single request message. Any additional requests that are to
                 * be made using this result object must be sent to the server as part of the same API request array.
                 */
                if (typeof request.result === 'function') {
                    const result = request.result();
                    returnResult = wrapReturnValue(result, self);
                    promise.result = () => returnResult;
                    assignExtraDataToReturnValue(returnResult, promise);

                    if (result.ObjectId && result.ObjectId.$var) {
                        promise
                            .then((resp) => {
                                // Sometimes the result of the response can be "undefined"
                                const responseResult = resp.result() || {};
                                result.ObjectId = responseResult.ObjectId;
                            })
                            .catch((err) => {
                                result.ObjectId = undefined;
                            });
                    }
                }

                promise.catch(self.rejectedPromiseHandler);

                return promise;
            });
        };
    }
}
